import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ShieldCheckIcon, ArrowLeftIcon, MailIcon } from 'lucide-react';
import { GlitterHeart } from '../components/GlitterHeart';
interface Section {
  title: string;
  content: React.ReactNode;
}
const sections: Section[] = [{
  title: '1. Information We Collect',
  content: <div className="space-y-5" data-id="element-2929" data-mp="2e1qogaybr1vd">
        <p data-id="element-2930" data-mp="2e0pe68p4ujep">We may collect the following types of information:</p>
        <div data-id="element-2931" data-mp="2is74m2tj51lf">
          <h4 className="font-bold text-plum-800 mb-2" data-id="element-2932" data-mp="2isz2xleihdfe">Personal Information</h4>
          <p className="mb-2" data-id="element-2933" data-mp="243knbvkcdzrk">You may voluntarily provide:</p>
          <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-2934" data-mp="245dyno5wh85w">
            <li data-id="element-2935" data-mp="293fhq93bt7hd">Name</li>
            <li data-id="element-2936" data-mp="292xbqkqv3ew9">Email address</li>
            <li data-id="element-2937" data-mp="1v34q33u6zov5">Phone number</li>
            <li data-id="element-2938" data-mp="1v3xo9gf3561o">Billing/payment information</li>
            <li data-id="element-2939" data-mp="1v58s1g8j6dss">Mailing address</li>
            <li data-id="element-2940" data-mp="1z4pwq7g4v6ww">Account login information</li>
            <li data-id="element-2941" data-mp="1z3op6cybt2jx">Questionnaire or onboarding responses</li>
            <li data-id="element-2942" data-mp="29qtrku7hjmrw">Appointment or scheduling details</li>
            <li data-id="element-2943" data-mp="29tg12zdxsccr">Travel preferences</li>
            <li data-id="element-2944" data-mp="24tbjf61albky">Coaching application information</li>
            <li data-id="element-2945" data-mp="24umm12j04dah">User-generated content, messages, reviews, or feedback</li>
          </ul>
        </div>
        <div data-id="element-2946" data-mp="2jngedojdgnwk">
          <h4 className="font-bold text-plum-800 mb-2" data-id="element-2947" data-mp="2eqpx4xvtzm9k">
            Automatically Collected Information
          </h4>
          <p className="mb-2" data-id="element-2948" data-mp="1r0lx10huq4nb">
            When you use our website or apps, we may automatically collect:
          </p>
          <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-2949" data-mp="1m33np8zx9nes">
            <li data-id="element-2950" data-mp="20rgg5vzswo4j">Device type</li>
            <li data-id="element-2951" data-mp="20shuqag0zsr0">Browser type</li>
            <li data-id="element-2952" data-mp="20srom946yk1z">IP address</li>
            <li data-id="element-2953" data-mp="1vzopzmkk4kex">App usage data</li>
            <li data-id="element-2954" data-mp="1vynfpkbd81y9">Pages visited</li>
            <li data-id="element-2955" data-mp="1vydq3xu8fwt3">Click activity</li>
            <li data-id="element-2956" data-mp="189ud5508mvuo">Referral sources</li>
            <li data-id="element-2957" data-mp="188t3n4yvehf2">Cookies and analytics data</li>
            <li data-id="element-2958" data-mp="13c2nyiqz9nu6">Approximate location information</li>
          </ul>
        </div>
        <div data-id="element-2959" data-mp="1szokrxbn6bsv">
          <h4 className="font-bold text-plum-800 mb-2" data-id="element-2960" data-mp="1szy97gh1gawg">
            Sensitive or Wellness-Related Information
          </h4>
          <p className="mb-2" data-id="element-2961" data-mp="1x08h4yoe5bdy">
            Some services may involve discussions related to:
          </p>
          <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-2962" data-mp="21tbcnccqauws">
            <li data-id="element-2963" data-mp="26rcvpxa5mu89">Mental wellness</li>
            <li data-id="element-2964" data-mp="26ql7x8msu0sj">Lifestyle habits</li>
            <li data-id="element-2965" data-mp="1a0u76rnwzhea">Stress management</li>
            <li data-id="element-2966" data-mp="1a1vrznuqfi97">Organization and productivity</li>
            <li data-id="element-2967" data-mp="1a36xbs3tsy2f">Personal goals</li>
          </ul>
          <p className="mt-2" data-id="element-2968" data-mp="1e3h0kx08hadl">
            We do not provide medical or mental health treatment services. Any
            information shared is voluntary.
          </p>
        </div>
      </div>
}, {
  title: '2. How We Use Your Information',
  content: <div data-id="element-2969" data-mp="2ugxplb7ujbwb">
        <p className="mb-3" data-id="element-2970" data-mp="2uhgyk2fiqpy7">We may use your information to:</p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-2971" data-mp="2uj1rxoq3k2uh">
          <li data-id="element-2972" data-mp="2zdxmowmpx4ba">Provide and improve our services</li>
          <li data-id="element-2973" data-mp="nsyfqoyeccub">Process purchases and subscriptions</li>
          <li data-id="element-2974" data-mp="nrnhf4npzxal">Deliver digital products, courses, or checklists</li>
          <li data-id="element-2975" data-mp="iudh2tysm17z">Schedule coaching sessions or consultations</li>
          <li data-id="element-2976" data-mp="e2c7xgwx8qm6">Respond to inquiries and support requests</li>
          <li data-id="element-2977" data-mp="e0sr9p7csdjv">Personalize app experiences</li>
          <li data-id="element-2978" data-mp="93snjkxz7mvx">Generate customized schedules, reminders, or recommendations</li>
          <li data-id="element-2979" data-mp="91ofqrwyi3qd">Provide website and app development services</li>
          <li data-id="element-2980" data-mp="53ige4yg6qfh">Deliver branding or business support services</li>
          <li data-id="element-2981" data-mp="2b721gnpj6p">Send newsletters, updates, promotions, or announcements</li>
          <li data-id="element-2982" data-mp="3r95lr24aam69">Improve website performance and user experience</li>
          <li data-id="element-2983" data-mp="3r9nmoc1w5oki">Monitor app functionality and prevent abuse</li>
          <li data-id="element-2984" data-mp="3maubwrdzobo1">Comply with legal obligations</li>
        </ul>
      </div>
}, {
  title: '3. Coaching & Educational Disclaimer',
  content: <div className="space-y-3" data-id="element-2985" data-mp="1pfodpxc8rjaz">
        <p data-id="element-2986" data-mp="1ki656824z63i">Trippin' with Mandy may offer:</p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-2987" data-mp="1kj7jcleg88pg">
          <li data-id="element-2988" data-mp="1skf4u60ae504">Coaching</li>
          <li data-id="element-2989" data-mp="1sk5ay7c4fdp5">Motivational content</li>
          <li data-id="element-2990" data-mp="1xi6t8q1q38zk">Wellness discussions</li>
          <li data-id="element-2991" data-mp="1xh5kis86iyl0">Organization systems</li>
          <li data-id="element-2992" data-mp="1xg45kco1lry0">Lifestyle guidance</li>
          <li data-id="element-2993" data-mp="1ilzcjzpiwpp1">Travel tips</li>
          <li data-id="element-2994" data-mp="1iko7zxo97dwv">Educational resources</li>
          <li data-id="element-2995" data-mp="1nkak23scio43">Business guidance</li>
        </ul>
        <p data-id="element-2996" data-mp="1nizhu8ejtof3">
          These services are for informational and educational purposes only and
          are not medical, legal, financial, or psychological advice.
        </p>
        <p data-id="element-2997" data-mp="1ek48ing7jy7k">
          If you are experiencing a medical or mental health emergency, please
          contact a licensed professional or emergency services.
        </p>
      </div>
}, {
  title: '4. Website, App Development & Business Services',
  content: <div className="space-y-3" data-id="element-2998" data-mp="qu080oybgeks">
        <p data-id="element-2999" data-mp="qtry95xhppdt">Trippin' with Mandy may provide:</p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-3000" data-mp="qw4khvaa6lwo">
          <li data-id="element-3001" data-mp="uv3ptgzxckon">Website design and development</li>
          <li data-id="element-3002" data-mp="c71i4yf3xs86">App development assistance</li>
          <li data-id="element-3003" data-mp="c7uucewu59xp">Branding and content creation</li>
          <li data-id="element-3004" data-mp="78885fz14jcr">AI-assisted business tools</li>
          <li data-id="element-3005" data-mp="79jijymzcpcu">Digital setup assistance</li>
          <li data-id="element-3006" data-mp="7aksu0w697ti">Business consulting and organizational support</li>
        </ul>
        <p data-id="element-3007" data-mp="l9aulttxrvo7">
          Clients may voluntarily provide business-related information, branding
          materials, login credentials, project requirements, content, or other
          materials necessary to complete requested services.
        </p>
        <p data-id="element-3008" data-mp="q7m586s22a7h">
          We take reasonable precautions to protect shared information; however,
          clients are responsible for maintaining the security of their own
          accounts, passwords, hosting platforms, third-party services, and
          connected tools.
        </p>
        <p data-id="element-3009" data-mp="1dwf348s8ms4q">Trippin' with Mandy is not responsible for:</p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-3010" data-mp="1dx6q4v7rrhje">
          <li data-id="element-3011" data-mp="18ymzihi8dhkp">Third-party platform outages</li>
          <li data-id="element-3012" data-mp="18vr2qwi4cyxu">Hosting failures</li>
          <li data-id="element-3013" data-mp="6kyvc29l6w0g">App store approval decisions</li>
          <li data-id="element-3014" data-mp="6m0gizkbgyvw">Third-party software limitations</li>
          <li data-id="element-3015" data-mp="akzlul9ymxnv">Data loss caused by external platforms or user actions</li>
          <li data-id="element-3016" data-mp="fkueat9dvvb1">Ongoing maintenance unless otherwise agreed upon</li>
        </ul>
        <p data-id="element-3017" data-mp="fire5geiolsl">
          Clients retain ownership of their original content and branding
          materials unless otherwise specified in writing.
        </p>
      </div>
}, {
  title: '5. Travel Services & Recommendations',
  content: <div className="space-y-3" data-id="element-3018" data-mp="u8ywlw4iy4ta">
        <p data-id="element-3019" data-mp="u7x9uue5btvq">
          Travel-related information, recommendations, packing guides,
          checklists, and destination suggestions are provided for general
          informational purposes only.
        </p>
        <p data-id="element-3020" data-mp="138c80uc7l3gn">Users are responsible for:</p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-3021" data-mp="3038arla56wgm">
          <li data-id="element-3022" data-mp="351tehuolry9t">Verifying travel requirements</li>
          <li data-id="element-3023" data-mp="352kye88u87k9">Reviewing airline/cruise policies</li>
          <li data-id="element-3024" data-mp="2xiadyajbfmdh">Obtaining travel insurance if desired</li>
          <li data-id="element-3025" data-mp="2xjv6xvpzex98">Confirming passport, visa, and vaccination requirements</li>
        </ul>
        <p data-id="element-3026" data-mp="2sqisufixi8uq">
          Trippin' with Mandy is not responsible for third-party travel
          disruptions, cancellations, delays, injuries, or losses.
        </p>
      </div>
}, {
  title: '6. Purchases & Payments',
  content: <div className="space-y-3" data-id="element-3027" data-mp="2irjjsvu94b77">
        <p data-id="element-3028" data-mp="3gho7wsh1ox3z">Payments may be processed through third-party providers such as:</p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-3029" data-mp="3bi1uoj17vgv6">
          <li data-id="element-3030" data-mp="3bfyvb8e6cbds">Stripe</li>
          <li data-id="element-3031" data-mp="36ioyv8sdazgg">PayPal</li>
          <li data-id="element-3032" data-mp="36i73sfhfyra4">Gumroad</li>
          <li data-id="element-3033" data-mp="36hdxffmea3si">Google Play</li>
          <li data-id="element-3034" data-mp="36ktegnzmpmwk">Apple App Store</li>
          <li data-id="element-3035" data-mp="32i52w8jj61vw">Other payment processors</li>
        </ul>
        <p data-id="element-3036" data-mp="32jg6a792d7mh">We do not store full payment card information on our servers.</p>
        <p data-id="element-3037" data-mp="1v5d2fiyxtj4b">
          Please review the privacy policies of any third-party payment provider
          you use.
        </p>
      </div>
}, {
  title: '7. Cookies & Analytics',
  content: <div className="space-y-3" data-id="element-3038" data-mp="3q5ezadpb3zjc">
        <p data-id="element-3039" data-mp="3v61ldqyroku5">We may use:</p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-3040" data-mp="3v6bbrfnq4u0d">
          <li data-id="element-3041" data-mp="3h6a1xaphec8r">Cookies</li>
          <li data-id="element-3042" data-mp="3h71xilj2w9z1">Pixels</li>
          <li data-id="element-3043" data-mp="3h7uskp8odb1c">Analytics tools</li>
          <li data-id="element-3044" data-mp="3h8mqi6pqvkus">Advertising technologies</li>
        </ul>
        <p data-id="element-3045" data-mp="3m662t12vtnk3">These tools help us:</p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-3046" data-mp="3m7h9b8npp9ew">
          <li data-id="element-3047" data-mp="dq36tsl4tnbl">Understand website traffic</li>
          <li data-id="element-3048" data-mp="hqwwaqe83a6l">Improve performance</li>
          <li data-id="element-3049" data-mp="hpv7lj4aap6e">Personalize experiences</li>
          <li data-id="element-3050" data-mp="hszhmx4fkf4z">Measure marketing effectiveness</li>
        </ul>
        <p data-id="element-3051" data-mp="3yypim7zhqdl">You may disable cookies through your browser settings.</p>
      </div>
}, {
  title: '8. Email & Marketing Communications',
  content: <div className="space-y-3" data-id="element-3052" data-mp="wnvyfyqb2ato">
        <p data-id="element-3053" data-mp="wnnn4b9tzdkl">
          If you subscribe to newsletters, download resources, create an
          account, make a purchase, or contact us through forms, we may send:
        </p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-3054" data-mp="1at61tkdbf4pu">
          <li data-id="element-3055" data-mp="16sm4ogsqs13p">Service updates</li>
          <li data-id="element-3056" data-mp="16rb38nmrr5fr">Promotional emails</li>
          <li data-id="element-3057" data-mp="16q9qmeq3uavx">Product announcements</li>
          <li data-id="element-3058" data-mp="1kkae2cblwbgz">App updates</li>
          <li data-id="element-3059" data-mp="1kifv33jwatfj">Event or livestream information</li>
          <li data-id="element-3060" data-mp="1fko020nyer74">Business or coaching communications</li>
        </ul>
        <p data-id="element-3061" data-mp="1flfxzi50x10k">
          You may unsubscribe at any time using the unsubscribe link in emails.
        </p>
      </div>
}, {
  title: '9. Third-Party Services',
  content: <div className="space-y-3" data-id="element-3062" data-mp="nutac2w6ozhx">
        <p data-id="element-3063" data-mp="nvuqumvyye71">
          Our services may integrate with third-party platforms, including but
          not limited to:
        </p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-3064" data-mp="wtn47li4rbrs">
          <li data-id="element-3065" data-mp="2d8g1x7xyigl1">Google services</li>
          <li data-id="element-3066" data-mp="2d7n2ks1butcx">Firebase</li>
          <li data-id="element-3067" data-mp="2d6v7dibn6xn6">TikTok</li>
          <li data-id="element-3068" data-mp="2da9a4ro7x8v5">Calendars</li>
          <li data-id="element-3069" data-mp="289uzr2z59mty">Payment processors</li>
          <li data-id="element-3070" data-mp="288jzhd4wqx7l">Scheduling systems</li>
          <li data-id="element-3071" data-mp="28bpjwf2ldawz">Social media platforms</li>
          <li data-id="element-3072" data-mp="1gjosqln6oony">Analytics providers</li>
          <li data-id="element-3073" data-mp="1ginbu0jhl7yb">Hosting providers</li>
          <li data-id="element-3074" data-mp="1bklx1rt3fusn">AI tools and automation platforms</li>
        </ul>
        <p data-id="element-3075" data-mp="1bl3x6zivmt5u">
          We are not responsible for the privacy practices of third-party
          services.
        </p>
      </div>
}, {
  title: '10. Data Security',
  content: <div className="space-y-3" data-id="element-3076" data-mp="11u91z5nnkj89">
        <p data-id="element-3077" data-mp="xsxhfejmyo6x">We take reasonable measures to protect your information using:</p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-3078" data-mp="xqul6crw3ytr">
          <li data-id="element-3079" data-mp="ss9kkc8q7d4s">Secure platforms</li>
          <li data-id="element-3080" data-mp="nurjt94v447v">Password protections</li>
          <li data-id="element-3081" data-mp="nvsvnftpcuqn">Encrypted services where available</li>
          <li data-id="element-3082" data-mp="nwkqupje0qge">Limited access controls</li>
        </ul>
        <p data-id="element-3083" data-mp="iwpysinvlutr">
          However, no method of internet transmission or storage is 100% secure.
        </p>
      </div>
}, {
  title: "11. Children's Privacy",
  content: <div className="space-y-3" data-id="element-3084" data-mp="2d9fxddp6vs7h">
        <p data-id="element-3085" data-mp="2dah7nfydsao5">
          Our services are not directed toward children under 13 years old. We
          do not knowingly collect personal information from children.
        </p>
        <p data-id="element-3086" data-mp="2pr8hscar3ia4">
          If we become aware that information from a child under 13 has been
          collected, we will delete it promptly.
        </p>
      </div>
}, {
  title: '12. Your Rights',
  content: <div className="space-y-3" data-id="element-3087" data-mp="2zhl7sk1c43nn">
        <p data-id="element-3088" data-mp="22ru6a0umlg8c">Depending on your location, you may have rights to:</p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-3089" data-mp="22tncxg58o0gc">
          <li data-id="element-3090" data-mp="26uh3yidz9vdg">Access your data</li>
          <li data-id="element-3091" data-mp="26vie8kn66du4">Correct inaccurate information</li>
          <li data-id="element-3092" data-mp="2bs92hv94ofqm">Request deletion of your data</li>
          <li data-id="element-3093" data-mp="2bull8ampmu4q">Opt out of marketing communications</li>
          <li data-id="element-3094" data-mp="2grvey2h4tryd">Request information about data usage</li>
        </ul>
        <p data-id="element-3095" data-mp="3n7nzv05919m9">To make a request, contact us using the information below.</p>
      </div>
}, {
  title: '13. User Content & Community Features',
  content: <div className="space-y-3" data-id="element-3096" data-mp="3s4xnqbczpd7z">
        <p data-id="element-3097" data-mp="32id1l05fj9qh">
          If you post content in public areas, communities, chats, livestreams,
          games, or interactive features, that information may be visible to
          other users.
        </p>
        <p data-id="element-3098" data-mp="3cgj15b3rk3s6">Please avoid sharing sensitive personal information publicly.</p>
      </div>
}, {
  title: '14. App Features & Notifications',
  content: <div className="space-y-3" data-id="element-3099" data-mp="37fmyf0v5enoc">
        <p data-id="element-3100" data-mp="2jrvkjstym2ky">Some apps or services may request permission for:</p>
        <ul className="list-disc list-inside space-y-1 text-plum-600 pl-2" data-id="element-3101" data-mp="2jq11kk290kji">
          <li data-id="element-3102" data-mp="2eulq1yrpr0q9">Push notifications</li>
          <li data-id="element-3103" data-mp="2er7outuscxke">Calendar syncing</li>
          <li data-id="element-3104" data-mp="2ti6qfkx7eluh">Location access</li>
          <li data-id="element-3105" data-mp="2tj9hbsuj0oae">Camera or photo uploads</li>
          <li data-id="element-3106" data-mp="2ori1ac8zy0yi">Contacts or reminders</li>
        </ul>
        <p data-id="element-3107" data-mp="2opngqz1n0auy">Permissions can typically be managed in your device settings.</p>
      </div>
}, {
  title: '15. Changes to This Policy',
  content: <div className="space-y-3" data-id="element-3108" data-mp="1w32olwdc7uqc">
        <p data-id="element-3109" data-mp="1w21b7l8umw5g">
          We may update this Privacy Policy periodically. Updated versions will
          be posted with a revised effective date.
        </p>
        <p data-id="element-3110" data-mp="gayu23mctp6w">
          Continued use of our services after changes means you accept the
          updated policy.
        </p>
      </div>
}, {
  title: '16. Contact Information',
  content: <div className="space-y-3" data-id="element-3111" data-mp="6kw286qyyha5">
        <p data-id="element-3112" data-mp="6lyqe6wwq5md">For questions, concerns, or privacy requests, contact:</p>
        <a href="mailto:trippinwithmandy@gmail.com" className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-blush-100 hover:bg-blush-200 text-plum-800 font-bold transition-colors" data-id="element-3113" data-mp="uaqccgjwlrqp">
          <MailIcon className="w-5 h-5" data-id="element-3114" data-mp="l9jr5u6b7sr4" />
          trippinwithmandy@gmail.com
        </a>
      </div>
}];
export function PrivacyPolicyPage() {
  return <div className="min-h-screen bg-gradient-to-b from-blush-50 via-white to-lavender-50 pt-28 pb-24" data-id="element-3115" data-mp="360sm9xso5pll">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3116" data-mp="3a2nmeyyv5wxs">
        {/* Back link */}
        <Link to="/" className="inline-flex items-center gap-2 text-plum-500 hover:text-plum-700 font-bold text-sm mb-8 transition-colors" data-id="element-3117" data-mp="3q90lhrpc35zq">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-3118" data-mp="3lgh74sito6tv" />
          Back to Home
        </Link>

        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-center mb-12" data-id="element-3119" data-mp="3gh4l29zrz3sc">
          <div className="flex justify-center mb-4" data-id="element-3120" data-mp="83xdnz7zgfvm">
            <GlitterHeart size={32} data-id="element-3121" data-mp="84gu1bhziw7j" />
          </div>
          <div className="w-16 h-16 rounded-2xl bg-blush-100 flex items-center justify-center mx-auto mb-6 shadow-sm" data-id="element-3122" data-mp="341h2l1m7qq3">
            <ShieldCheckIcon className="w-8 h-8 text-cta-500" data-id="element-3123" data-mp="3v7bxa7z6vqkl" />
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-plum-800 mb-3" data-id="element-3124" data-mp="2nvbhhy6nnl4i">
            Privacy Policy
          </h1>
          <p className="text-plum-500 font-medium" data-id="element-3125" data-mp="2jrn777qn2bfk">For Trippin' with Mandy</p>
          <p className="text-plum-400 text-sm font-medium mt-1" data-id="element-3126" data-mp="zbyybmjf5ezs">
            Effective Date: May 12, 2026
          </p>
        </motion.div>

        {/* Intro */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="bg-white rounded-3xl p-8 shadow-sm border border-blush-100 mb-8" data-id="element-3127" data-mp="18bubty0g5m2z">
          <p className="text-plum-700 leading-relaxed mb-4" data-id="element-3128" data-mp="1mzyw36f5e5nv">
            Welcome to Trippin' with Mandy. Your privacy matters to us. This
            Privacy Policy explains how we collect, use, store, and protect your
            information across our websites, apps, digital products, coaching
            services, travel-related services, business services, games,
            communities, and related platforms operated under the Trippin' with
            Mandy brand, including but not limited to Trippin' Through Life
            (TTL) and affiliated projects.
          </p>
          <p className="text-plum-700 leading-relaxed font-medium" data-id="element-3129" data-mp="zzhysh6foxem">
            By using our services, you agree to the practices described in this
            Privacy Policy.
          </p>
        </motion.div>

        {/* Sections */}
        <div className="space-y-6" data-id="element-3130" data-mp="18z51d6awg999">
          {sections.map((section, idx) => <motion.section key={idx} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: idx * 0.03
        }} className="bg-white rounded-3xl p-8 shadow-sm border border-blush-100" data-id="element-3131" data-mp="1nw5g897zs9qb" data-mpi={idx}>
              <h2 className="font-heading text-2xl font-bold text-plum-800 mb-4" data-id="element-3132" data-mp="21pd8m33wd992">
                {section.title}
              </h2>
              <div className="text-plum-600 leading-relaxed space-y-3" data-id="element-3133" data-mp="2lawybryg4d9l">
                {section.content}
              </div>
            </motion.section>)}
        </div>

        {/* Closing */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="text-center mt-12 text-plum-500 text-sm font-medium italic px-4" data-id="element-3134" data-mp="2bby0l4f7y21d">
          By using Trippin' with Mandy services, websites, apps, products,
          games, or digital platforms, you acknowledge that you have read and
          agreed to this Privacy Policy.
        </motion.div>

        {/* Footer links */}
        <div className="text-center mt-10 flex flex-wrap justify-center gap-3" data-id="element-3135" data-mp="1jla0lvpa72pa">
          <Link to="/terms" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-plum-200 text-plum-700 hover:bg-plum-50 font-bold text-sm transition-colors" data-id="element-3136" data-mp="1jne2y97j7tng">
            View Terms & Conditions
          </Link>
          <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-cta-500 text-white hover:bg-cta-600 font-bold text-sm transition-colors shadow-lg shadow-cta-500/20" data-id="element-3137" data-mp="1z0ykoo7m1fwi">
            Back to Home
          </Link>
        </div>
      </div>
    </div>;
}